import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Collapse,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  styled
} from '@mui/material';
import {
  School as ScholarshipIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  AttachFile as FileIcon
} from '@mui/icons-material';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.23)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00BCD4',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
    '&.Mui-focused': {
      color: '#00BCD4',
    },
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(3),
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ScholarshipGrantApplicationForm = ({ onSubmit, projects, isSubmitting }) => {
  const [formData, setFormData] = useState({
    applicantName: '',
    email: '',
    phone: '',
    projectId: '',
    amount: '',
    purpose: '',
    academicRecord: '',
    financialNeed: '',
    references: [],
    attachments: []
  });
  
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectionCriteria, setSelectionCriteria] = useState([]);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate IRS compliance
  const validateIRSCompliance = async (data) => {
    // Implement IRS compliance validation logic here
    return true;
  };

  // Validate project funds
  const validateProjectFunds = async (projectId, amount) => {
    // Implement project funds validation logic here
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Validate compliance and funds
      const isIRSCompliant = await validateIRSCompliance(formData);
      const hasSufficientFunds = await validateProjectFunds(formData.projectId, formData.amount);

      if (!isIRSCompliant) {
        throw new Error('Application does not meet IRS compliance requirements');
      }

      if (!hasSufficientFunds) {
        throw new Error('Insufficient project funds for this scholarship');
      }

      await onSubmit(formData);
      setSuccess('Scholarship application submitted successfully!');
      resetForm();
    } catch (err) {
      setError(err.message);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      applicantName: '',
      email: '',
      phone: '',
      projectId: '',
      amount: '',
      purpose: '',
      academicRecord: '',
      financialNeed: '',
      references: [],
      attachments: []
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ color: '#00BCD4', mb: 2 }}>
            <ScholarshipIcon sx={{ mr: 1 }} />
            Scholarship Application
          </Typography>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Alert 
              severity="error"
              action={
                <IconButton size="small" onClick={() => setError(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          </Grid>
        )}

        {success && (
          <Grid item xs={12}>
            <Alert 
              severity="success"
              action={
                <IconButton size="small" onClick={() => setSuccess(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {success}
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Applicant Name"
            name="applicantName"
            value={formData.applicantName}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Project"
            name="projectId"
            select
            value={formData.projectId}
            onChange={handleChange}
            required
          >
            {projects.map((project) => (
              <MenuItem key={project._id} value={project._id}>
                {project.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Amount Requested"
            name="amount"
            type="number"
            value={formData.amount}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Purpose of Scholarship"
            name="purpose"
            multiline
            rows={4}
            value={formData.purpose}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#00BCD4',
              '&:hover': {
                backgroundColor: '#00ACC1',
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 188, 212, 0.5)',
              }
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ScholarshipGrantApplicationForm; 